import { AppShell } from '@raas/components/b2b/app-shell';
import { SystemLogo } from '@raas/components/dashboard/system-logo';
import { Toaster } from '@raas/components/ui/sonner';
import { SidebarOrgSwitcher } from '@raas/cornerstone-auth/components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Cpu } from 'lucide-react';
import { lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import AuthScreen from './AuthScreen';
import { buildBreadcrumbs, buildMenuItems } from './lib/app-shell-config';
import { AppSessionProvider, useAppSession } from './session';

const MarketPage = lazy(() => import('./pages/MarketPage'));
const CreatePage = lazy(() => import('./pages/CreatePage'));
const InstancesPage = lazy(() => import('./pages/InstancesPage'));
const InstanceDetailPage = lazy(() => import('./pages/InstanceDetailPage'));
const ImagesPage = lazy(() => import('./pages/ImagesPage'));
const StoragePage = lazy(() => import('./pages/StoragePage'));
const BillingBalancePage = lazy(() => import('./pages/BillingBalancePage'));
const BillingRecordsPage = lazy(() => import('./pages/BillingRecordsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

function LoadingState() {
  return (
    <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">
      加载中...
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAppSession();
  const location = useLocation();

  if (isLoading) return <LoadingState />;

  if (!isAuthenticated) {
    const returnTo = encodeURIComponent(
      `${location.pathname}${location.search || ''}`,
    );
    return <Navigate replace to={`/login?returnTo=${returnTo}`} />;
  }

  return <>{children}</>;
}

function LoginRoute() {
  const { isAuthenticated, isLoading } = useAppSession();
  const location = useLocation();
  const returnTo =
    new URLSearchParams(location.search).get('returnTo') ?? '/market';

  if (isLoading) return <LoadingState />;
  if (isAuthenticated) return <Navigate replace to={returnTo} />;

  return <AuthScreen returnTo={returnTo} />;
}

function AppLayout() {
  const {
    activeOrganization,
    isSwitchingOrganization,
    logout,
    organizations,
    session,
    memberRole,
    switchOrganization,
  } = useAppSession();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <AppShell
      currentServiceId="tensor-pod"
      orgId={activeOrganization?.id ?? ''}
      logo={<SystemLogo icon={Cpu} title="TensorPod" subtitle="弹性算力平台" />}
      orgSwitcher={
        <SidebarOrgSwitcher
          activeOrganizationId={activeOrganization?.id ?? null}
          disabled={isSwitchingOrganization}
          organizations={organizations}
          onChange={switchOrganization}
          onNavigate={(path) => navigate(path)}
        />
      }
      menuItems={buildMenuItems(location.pathname, {
        isAdmin: session?.user?.role === 'admin',
      })}
      user={{
        email: session?.user?.email ?? '',
        name: session?.user?.name ?? memberRole ?? '用户',
      }}
      breadcrumbs={buildBreadcrumbs(location.pathname)}
      accountLogoutLabel="退出登录"
      onLogout={() => {
        void logout().then(() => navigate('/login', { replace: true }));
      }}
      onNavigate={(path) => navigate(path)}
    >
      <Suspense fallback={<LoadingState />}>
        <Routes>
          <Route path="/" element={<Navigate replace to="/market" />} />
          <Route path="/market" element={<MarketPage />} />
          <Route path="/create" element={<CreatePage />} />
          <Route path="/instances" element={<InstancesPage />} />
          <Route path="/instances/:id" element={<InstanceDetailPage />} />
          <Route path="/images" element={<ImagesPage />} />
          <Route path="/storage" element={<StoragePage />} />
          <Route path="/billing/balance" element={<BillingBalancePage />} />
          <Route path="/billing/records" element={<BillingRecordsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<Navigate replace to="/market" />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppSessionProvider>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route
              path="/*"
              element={
                <RequireAuth>
                  <AppLayout />
                </RequireAuth>
              }
            />
          </Routes>
        </AppSessionProvider>
      </BrowserRouter>
      <Toaster position="top-center" />
    </QueryClientProvider>
  );
}
